import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, ShieldAlert } from "lucide-react";

// ---------------------------------------------------------------------------
// Universal Super Admin "Delete Record" affordance.
//
// Universal contract enforced by this component:
//   * A deletion REASON is mandatory (dropdown, 6 canonical choices).
//     When "Other" is selected, a short free-text explanation is also
//     mandatory.
//   * TYPED "DELETE" confirmation is required for records that carry
//     dependencies, uploaded assets, contractual significance, or a
//     cascade effect. Simple standalone records (no `confirmToken`
//     prop supplied) may proceed on reason alone.
//   * Deletion reason is transmitted as `?reason=<text>` and (for
//     Super Admin overrides on records with operational history)
//     `?force=true` in the query string. Backend RBAC is the source of
//     truth.
//   * On success, the record is removed from the UI via `onDeleted`.
//     On failure a clear toast is shown; the audit tombstone still
//     lands server-side for successful deletes.
// ---------------------------------------------------------------------------

// The six canonical deletion reasons. Do NOT change wording — audit
// downstreams depend on these exact strings for reporting.
export const DELETE_REASON_CHOICES = [
  { value: "Test record",         label: "Test record" },
  { value: "Duplicate",           label: "Duplicate" },
  { value: "Entered in error",    label: "Entered in error" },
  { value: "Client request",      label: "Client request" },
  { value: "No longer required",  label: "No longer required" },
  { value: "Other",               label: "Other (please explain)" },
];

export function DeleteRecordButton({
  recordType,              // "demo" | "submission" | "license_request" | ...
  recordLabel,             // Human label ("Demo", "Track", …)
  endpoint,                // e.g. `/demos/${id}`
  confirmToken,            // Optional: typed-token to unlock (dependencies)
  confirmTokenLabel,       // How to describe the token to the user
  disabled,
  disabledReason,
  onDeleted,               // callback after successful delete
  testidPrefix,
  isSuperAdmin,            // when true, sends ?force=true to bypass hard-delete guards
  requireTypedConfirmation, // force typed-DELETE gate even without confirmToken
  cascadePreview,          // optional {label,count}[] describing what will also be removed
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const typedTokenNeeded = useMemo(() => {
    return !!confirmToken || !!requireTypedConfirmation;
  }, [confirmToken, requireTypedConfirmation]);

  const typedOk = useMemo(() => {
    if (!typedTokenNeeded) return true;
    const expected = confirmToken || "DELETE";
    return (typed || "").trim().toLowerCase() === expected.trim().toLowerCase();
  }, [typedTokenNeeded, typed, confirmToken]);

  const reasonOk = useMemo(() => {
    if (!reason) return false;
    if (reason === "Other") return (otherText || "").trim().length >= 3;
    return true;
  }, [reason, otherText]);

  const canProceed = !disabled && typedOk && reasonOk;

  function resetState() {
    setTyped("");
    setReason("");
    setOtherText("");
  }

  async function handleConfirm() {
    if (!canProceed || submitting) return;
    setSubmitting(true);
    try {
      const finalReason = reason === "Other"
        ? `Other: ${(otherText || "").trim()}`
        : reason;
      const qs = new URLSearchParams();
      qs.set("reason", finalReason);
      if (isSuperAdmin) qs.set("force", "true");
      await api.delete(`${endpoint}?${qs.toString()}`);
      toast.success(`${recordLabel} deleted`);
      setOpen(false);
      resetState();
      onDeleted?.();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg;
      if (typeof raw === "string") msg = raw;
      else if (raw && typeof raw === "object") msg = raw.message || JSON.stringify(raw);
      else msg = e?.message || "Delete failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const testid = testidPrefix || `delete-${recordType}`;
  const tokenExpected = confirmToken || (typedTokenNeeded ? "DELETE" : "");

  return (
    <div
      className="mt-6 rounded-lg border border-[hsl(0_70%_42%)]/25 bg-[hsl(0_70%_98%)] p-4"
      data-testid={`${testid}-card`}
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "hsl(0 70% 42%)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium" data-testid={`${testid}-title`}>
            Delete this {recordLabel.toLowerCase()}
          </div>
          <p className="text-[11.5px] text-muted-foreground mt-1 leading-relaxed">
            Removes this record from active queues, dashboards, and API responses.
            A minimal audit tombstone (who, when, record type, ID, reason) is
            preserved so the deletion itself remains traceable, but the record
            content is no longer visible in ordinary views. This cannot be
            undone.
          </p>
          {disabled && disabledReason && (
            <p
              className="text-[11.5px] mt-2 text-[hsl(0_70%_38%)] bg-white/60 border border-[hsl(0_70%_42%)]/25 rounded px-2 py-1"
              data-testid={`${testid}-disabled-reason`}
            >
              {disabledReason}
            </p>
          )}
          <div className="mt-3">
            <Button
              variant="outline"
              disabled={disabled || submitting}
              onClick={() => setOpen(true)}
              className="border-[hsl(0_70%_42%)]/40 text-[hsl(0_70%_38%)] hover:bg-[hsl(0_70%_95%)] hover:text-[hsl(0_70%_32%)]"
              data-testid={`${testid}-open`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Delete {recordLabel.toLowerCase()}…
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetState(); }}>
        <AlertDialogContent data-testid={`${testid}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {recordLabel.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the {recordLabel.toLowerCase()} from active
              views. A minimal audit tombstone (who, when, type, ID, reason) is
              retained. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {Array.isArray(cascadePreview) && cascadePreview.length > 0 && (
            <div
              className="rounded-md border bg-secondary/50 p-2 text-[11.5px] leading-relaxed"
              data-testid={`${testid}-cascade`}
            >
              <div className="font-medium mb-1">The following will also be removed:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {cascadePreview.map((row, i) => (
                  <li key={i}>
                    {row.label}
                    {typeof row.count === "number" ? ` (${row.count})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-3 py-2">
            {typedTokenNeeded && (
              <div>
                <Label className="text-[11.5px]">
                  Type{" "}
                  <span className="font-mono px-1 py-0.5 rounded bg-secondary">
                    {tokenExpected}
                  </span>
                  {confirmTokenLabel ? ` (${confirmTokenLabel})` : ""} to confirm
                </Label>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoFocus
                  className="mt-1 font-mono"
                  placeholder={tokenExpected}
                  data-testid={`${testid}-confirm-input`}
                />
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  className="mt-1 text-[12.5px]"
                  data-testid={`${testid}-reason-trigger`}
                >
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`${testid}-reason-${r.value.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  rows={2}
                  placeholder="Briefly explain why this record is being deleted…"
                  className="mt-1 text-[12.5px]"
                  data-testid={`${testid}-reason-other`}
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canProceed || submitting}
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              className="bg-[hsl(0_70%_42%)] hover:bg-[hsl(0_70%_38%)] text-white"
              data-testid={`${testid}-confirm`}
            >
              {submitting ? "Deleting…" : `Delete ${recordLabel}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default DeleteRecordButton;
